import { useRef } from 'react'
import { motion, useScroll, useTransform } from 'framer-motion'
import { ArrowUpRight } from 'lucide-react'

export type ParallaxCase = {
  eyebrow: string
  title: string
  description: string
  imageUrl: string
  href?: string
  reverse?: boolean
  metrics?: Array<{ label: string; value: string }>
}

type ParallaxItemProps = {
  section: ParallaxCase
  index: number
}

function ParallaxItem({ section, index }: ParallaxItemProps) {
  const ref = useRef<HTMLDivElement>(null)
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start end', 'center start'],
  })

  const opacity = useTransform(scrollYProgress, [0, 0.65], [0, 1])
  const clipPath = useTransform(
    scrollYProgress,
    [0, 0.65],
    ['inset(0 100% 0 0)', 'inset(0 0% 0 0)'],
  )
  const y = useTransform(scrollYProgress, [0, 1], [-40, 0])

  return (
    <div
      ref={ref}
      className={`flex min-h-screen flex-col items-center justify-center gap-10 px-5 sm:px-8 md:flex-row md:gap-20 lg:gap-32 ${
        section.reverse ? 'md:flex-row-reverse' : ''
      }`}
    >
      {/* Text side */}
      <motion.div style={{ y }} className="max-w-sm flex-shrink-0 md:max-w-xs lg:max-w-sm">
        <p className="font-mono text-xs font-bold uppercase tracking-[0.22em] text-sky-300">
          {section.eyebrow}
        </p>
        <h2 className="mt-4 text-4xl font-black leading-[1.05] tracking-tight text-white lg:text-5xl">
          {section.title}
        </h2>
        <motion.p style={{ y }} className="mt-6 leading-7 text-slate-400">
          {section.description}
        </motion.p>

        {section.metrics && section.metrics.length > 0 && (
          <div className="mt-8 grid grid-cols-2 gap-3">
            {section.metrics.map((m) => (
              <div
                key={m.label}
                className="rounded-xl border border-slate-800 bg-slate-900/80 px-4 py-3"
              >
                <span className="block text-2xl font-black text-sky-300">{m.value}</span>
                <span className="mt-1 block text-xs text-slate-500">{m.label}</span>
              </div>
            ))}
          </div>
        )}

        {section.href && (
          <a
            href={section.href}
            className="group mt-8 inline-flex items-center gap-2 text-sm font-semibold text-sky-300 transition hover:text-sky-200"
          >
            Read case study
            <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
        )}
      </motion.div>

      {/* Image side */}
      <motion.div
        style={{ opacity, clipPath }}
        className="relative flex-shrink-0"
      >
        <div className="absolute -inset-px rounded-2xl border border-sky-300/20" />
        <img
          src={section.imageUrl}
          alt={section.title}
          className="h-72 w-72 rounded-2xl object-cover shadow-[0_32px_80px_rgba(2,6,23,0.5)] sm:h-80 sm:w-80 lg:h-96 lg:w-96"
        />
        {/* Sky glow */}
        <div className="pointer-events-none absolute -inset-8 rounded-3xl bg-sky-300/5 blur-2xl" />
        {/* Index watermark */}
        <span className="absolute -bottom-4 -right-4 font-mono text-7xl font-black leading-none text-slate-800 select-none">
          0{index + 1}
        </span>
      </motion.div>
    </div>
  )
}

type ParallaxScrollSectionProps = {
  eyebrow?: string
  heading?: string
  cases: ParallaxCase[]
}

export function ParallaxScrollSection({
  eyebrow = 'Case Studies',
  heading = 'Work that moved the needle.',
  cases,
}: ParallaxScrollSectionProps) {
  return (
    <section className="relative border-y border-slate-800 bg-slate-950">
      {/* Section header */}
      <div className="mx-auto max-w-7xl px-5 pt-20 sm:px-8">
        <p className="section-kicker">{eyebrow}</p>
        <h2 className="section-title max-w-xl">{heading}</h2>
      </div>

      {/* Parallax items */}
      <div className="mx-auto max-w-7xl">
        {cases.map((section, index) => (
          <ParallaxItem key={section.title} section={section} index={index} />
        ))}
      </div>
    </section>
  )
}
